import { Graphics, Sprite, Text } from 'pixi.js';
import { Signal } from 'typed-signals';
import gsap from 'gsap';
import { Shared } from '../App';
import { NineSliceButton } from './buttons/NineSliceButton';
import { NineSlicePurchaseButton } from './buttons/NineSlicePurchaseButton';
import DiamondSprite from './DiamondSprite';

const defaultOpts = {
    coinBtnAccessible: false,
    diamondBtnAccessible: false,
    cancelBtnAccessible: false,
    scale: 1,
    unlockCoinCost: 100,
    unlockDiamondCost: 5,
    enableCoinPurchase: true,
    enableDiamondPurchase: true,
    enableCancel: true,
};

export type PurchasePanelOpts = typeof defaultOpts;

export class PurchasePanel
{
    public view: Sprite;

    public signals: {
        coinBtnClick: Signal<(val: number) => void>
        diamondBtnClick: Signal<(val: number) => void>
        cancelBtnClick: Signal<() => void>
    };

    protected bg: Graphics;
    protected coinBtn?: NineSlicePurchaseButton;
    protected diamondBtn?: NineSlicePurchaseButton;
    protected cancelBtn?: NineSliceButton;

    private _width;

    constructor(opts: Partial<PurchasePanelOpts> = {})
    {
        const o = { ...defaultOpts, ...opts };

        this.signals = {
            coinBtnClick: new Signal<(val: number) => void>(),
            diamondBtnClick: new Signal<(val: number) => void>(),
            cancelBtnClick: new Signal<() => void>(),
        };

        const purchaseBtnWidth = 120;
        const purchaseBtnHeight = 50;
        const padding = 5;

        this.view = new Sprite();
        this.view.anchor.set(0.5);

        let totalWidth = padding;

        if (o.enableCoinPurchase && o.unlockCoinCost > 0)
        {
            const locked = Shared.game.garage.user.data.coins < o.unlockCoinCost;
            const coinIcon = Sprite.from(Shared.assets.getTexture('coins-small'));
            const coinPrice = new Text(String(o.unlockCoinCost), {
                fontFamily: 'lubalin',
                fill: locked ? 0xec938d : 0xFFFFFF,
                stroke: 0x333333,
                strokeThickness: 6,
                lineJoin: 'round',
                align: 'center',
                fontSize: 26,
            });

            coinPrice.anchor.set(1, 0.5);
            coinPrice.x = (purchaseBtnWidth / 2) - 5;
            coinPrice.y = -2;

            coinIcon.anchor.set(0, 0.5);
            coinIcon.x = 5 - (purchaseBtnWidth / 2) + (coinIcon.width * 0.5);

            this.coinBtn = new NineSlicePurchaseButton({
                icon: coinIcon,
                accessibleTitle: 'Back',
                bgAsset: 'button-ninesliced-green-clean',
                w: purchaseBtnWidth,
                h: purchaseBtnHeight,
                locked,
                hoverScale: 1.05,
            });
            this.coinBtn.view.addChild(coinIcon);
            this.coinBtn.view.addChild(coinPrice);

            coinPrice.alpha = locked ? 0.5 : 1;
            coinIcon.alpha = locked ? 0.5 : 1;

            Shared.game.hud.coinsWidget.valueUpdated.connect((newVal) =>
            {
                if (this.coinBtn) this.coinBtn.locked = newVal < o.unlockCoinCost;
                coinPrice.alpha = newVal < o.unlockCoinCost ? 0.5 : 1;
                coinIcon.alpha = newVal < o.unlockCoinCost ? 0.5 : 1;
                coinPrice.style.fill = newVal < o.unlockCoinCost ? 0xec938d : 0xFFFFFF;
            });

            this.coinBtn.accessible = o.coinBtnAccessible;
            this.coinBtn.view.position.set(
                totalWidth + purchaseBtnHeight + (2 * padding),
                (purchaseBtnHeight / 2) + padding,
            );
            totalWidth += purchaseBtnWidth + padding;

            this.coinBtn.onPress.connect(() => this.signals.coinBtnClick.emit(o.unlockCoinCost));
        }

        if (o.enableDiamondPurchase && o.unlockDiamondCost > 0)
        {
            const locked =  Shared.game.garage.user.data.diamonds < o.unlockDiamondCost;

            const diamondIcon = DiamondSprite.from(Shared.assets.getTexture('diamond-small'));
            const diamondPrice = new Text(String(o.unlockDiamondCost), {
                fontFamily: 'lubalin',
                fill: locked ? 0xec938d : 0xFFFFFF,
                stroke: 0x333333,
                strokeThickness: 6,
                lineJoin: 'round',
                align: 'center',
                fontSize: 26,
            });

            diamondPrice.anchor.set(1, 0.5);
            diamondPrice.x = (purchaseBtnWidth / 2) - 15;
            diamondPrice.y = -2;

            diamondIcon.anchor.set(0, 0.5);
            diamondIcon.x = 10 - (purchaseBtnWidth / 2);

            this.diamondBtn = new NineSlicePurchaseButton({
                icon: new Sprite(),
                accessibleTitle: 'Back',
                bgAsset: 'button-ninesliced-green-clean',
                w: purchaseBtnWidth,
                h: purchaseBtnHeight,
                locked,
                hoverScale: 1.05,
            });
            this.diamondBtn.view.addChild(diamondIcon);
            this.diamondBtn.view.addChild(diamondPrice);

            diamondPrice.alpha = locked ? 0.5 : 1;
            diamondIcon.alpha = locked ? 0.5 : 1;

            Shared.game.hud.diamondWidget.valueUpdated.connect((newVal) =>
            {
                if (this.diamondBtn) this.diamondBtn.locked = newVal < o.unlockDiamondCost;
                diamondPrice.alpha = newVal < o.unlockDiamondCost ? 0.5 : 1;
                diamondIcon.alpha = newVal < o.unlockDiamondCost ? 0.5 : 1;
                diamondPrice.style.fill = newVal < o.unlockCoinCost ? 0xec938d : 0xFFFFFF;
            });

            this.diamondBtn.accessible = o.diamondBtnAccessible;
            this.diamondBtn.view.position.set(
                totalWidth + purchaseBtnHeight + (2 * padding),
                (purchaseBtnHeight / 2) + padding,
            );
            totalWidth += purchaseBtnWidth + padding;

            this.diamondBtn.onPress.connect(() => this.signals.diamondBtnClick.emit(o.unlockDiamondCost));
        }

        if (o.enableCancel)
        {
            const cancelIcon = Sprite.from(Shared.assets.getTexture('icon-close'));

            cancelIcon.scale.set(0.5);

            this.cancelBtn = new NineSliceButton({
                icon: cancelIcon,
                accessibleTitle: 'Back',
                bgAsset: 'button-ninesliced-magenta',
                w: purchaseBtnHeight,
                h: purchaseBtnHeight,
            });

            this.cancelBtn.accessible = o.cancelBtnAccessible;
            this.cancelBtn.view.position.set(
                totalWidth + (purchaseBtnHeight / 2),
                (purchaseBtnHeight / 2) + padding,
            );
            totalWidth += purchaseBtnHeight + padding;

            this.cancelBtn.onPress.connect(() => this.signals.cancelBtnClick.emit());
        }

        this.bg = new Graphics();
        this.bg.beginFill(0xFFFFFF, 1);
        this.bg.drawRoundedRect(
            0,
            0,
            totalWidth,
            (purchaseBtnHeight) + (2 * padding),
            10,
        );
        this.bg.endFill();
        this.bg.position.set(-totalWidth / 2, -purchaseBtnHeight / 2);

        this.view.addChild(this.bg);
        this.bg.sortableChildren = true;
        if (this.coinBtn) this.bg.addChild(this.coinBtn.view);
        if (this.diamondBtn) this.bg.addChild(this.diamondBtn.view);
        if (this.cancelBtn) this.bg.addChild(this.cancelBtn.view);

        this._width = totalWidth - padding;
        
        this.setVisible(true);
    }
    
    setVisible(visible: boolean){
        this.view.visible = visible;
        
        gsap.killTweensOf(this.view.scale);
        
        if(visible){
            
            this.view.scale.set(0.5);

            gsap.to(this.view.scale, {
                duration: 0.3,
                x: 1,
                y: 1,
                ease: 'back.out(3)',
            });
        }
    }

    get width(): number { return this._width; }
}
